import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays product volume pricing tiers and keeps the PDP
 * price in sync with the currently selected quantity.
 *
 * It listens for two theme events within its section:
 * 1. `quantity-selector:update` - highlights the active tier and overrides the
 *    displayed price when a quantity threshold is reached.
 * 2. `variant:update` - re-renders its own markup from the server response, since
 *    tiers and eligibility depend on the selected variant.
 *
 * Tier prices are formatted server-side (via the Liquid `money` filter) and read
 * from row data attributes, so no client-side money formatting is required.
 */
class VolumePricing extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  /** @type {Array<{ min: number, priceCents: number, priceFormatted: string, row: HTMLElement }>} */
  #tiers = [];

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    if (!this.#section) return;

    this.#rebuildTiers();

    this.#section.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityChange,
    );
    this.#section.addEventListener(ThemeEvents.variantUpdate, this.#onVariant);

    this.#apply(this.#currentQuantity());
    this.#syncHost();
  }

  disconnectedCallback() {
    if (!this.#section) return;
    this.#section.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityChange,
    );
    this.#section.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariant,
    );
  }

  /**
   * Reads the tier rows from the DOM into a sorted lookup.
   */
  #rebuildTiers() {
    const rows = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-vp-row]'),
    ]);

    this.#tiers = rows
      .map((row) => ({
        min: parseInt(row.dataset.vpMin ?? '', 10),
        priceCents: parseInt(row.dataset.vpPriceCents ?? '', 10),
        priceFormatted: row.dataset.vpPriceFormatted ?? '',
        row,
      }))
      .filter((tier) => Number.isFinite(tier.min))
      .sort((a, b) => a.min - b.min);
  }

  /**
   * Gets the current quantity from the section's quantity selector.
   * @returns {number} The current quantity, defaulting to 1.
   */
  #currentQuantity() {
    const input = this.#section?.querySelector(
      'quantity-selector-component [ref="quantityInput"], quantity-selector-component input[type="number"]',
    );

    if (!(input instanceof HTMLInputElement)) return 1;
    return parseInt(input.value, 10) || 1;
  }

  /**
   * Handles quantity selector updates.
   * @param {Event} event - The quantity selector update event.
   */
  #onQuantityChange = (event) => {
    const detail = /** @type {{ quantity?: number }} */ (
      /** @type {CustomEvent} */ (event).detail ?? {}
    );
    const quantity = Number(detail.quantity);
    this.#apply(Number.isFinite(quantity) ? quantity : this.#currentQuantity());
  };

  /**
   * Handles variant updates by re-rendering from the server response. The block
   * is always present in the response (hidden when there are no qualifying tiers),
   * so tiers and eligibility stay correct per variant.
   * @param {Event} event - The variant update event.
   */
  #onVariant = (event) => {
    const detail = /** @type {any} */ (
      /** @type {CustomEvent} */ (event).detail
    );

    if (detail?.data?.newProduct?.id) {
      this.dataset.productId = detail.data.newProduct.id;
    }
    /** @type {Document | undefined} */
    const html = detail?.data?.html;
    const incoming = /** @type {HTMLElement | null} */ (
      html?.querySelector('tw-volume-pricing') ?? null
    );

    // product-price replaces the price container on the same event; defer so we
    // read/override the freshly rendered price rather than the stale one.
    requestAnimationFrame(() => {
      if (incoming) {
        this.innerHTML = incoming.innerHTML;
        this.hidden = incoming.hasAttribute('hidden');
        this.dataset.vpOnSale = incoming.dataset.vpOnSale ?? '';
      } else {
        this.innerHTML = '';
        this.hidden = true;
        this.dataset.vpOnSale = '';
      }

      this.#rebuildTiers();
      this.#apply(this.#currentQuantity());
      this.#syncHost();
    });
  };

  /**
   * Hides or shows an optional host wrapper (e.g. an accordion) marked with
   * `[data-vp-accordion]`, so it collapses entirely when no tiers qualify.
   * No-op when the element is not wrapped, e.g. as a standalone block.
   */
  #syncHost() {
    const host = this.closest('[data-vp-accordion]');
    if (host instanceof HTMLElement) host.hidden = this.#tiers.length === 0;
  }

  /**
   * Finds the highest tier whose threshold is met by the quantity.
   * @param {number} quantity - The current quantity.
   * @returns {{ min: number, priceCents: number, priceFormatted: string, row: HTMLElement } | null}
   */
  #activeTier(quantity) {
    let active = null;
    for (const tier of this.#tiers) {
      if (quantity >= tier.min) active = tier;
    }
    return active;
  }

  /**
   * Applies the active tier: highlights the row, overrides the price, announces.
   * @param {number} quantity - The current quantity.
   */
  #apply(quantity) {
    const active = this.#activeTier(quantity);

    for (const tier of this.#tiers) {
      tier.row.toggleAttribute('data-vp-active', tier === active);
    }

    this.#updatePrice(active);
    this.#announce(active);
  }

  /**
   * Returns the product-price elements within the section for this product.
   * @returns {HTMLElement[]} The matching product-price elements.
   */
  #priceElements() {
    if (!this.#section) return [];

    const scoped = this.#section.querySelectorAll(
      `product-price[data-product-id="${this.dataset.productId}"]`,
    );
    const elements = scoped.length
      ? scoped
      : this.#section.querySelectorAll('product-price');

    return /** @type {HTMLElement[]} */ ([...elements]);
  }

  /**
   * Overrides (or restores) the displayed price based on the active tier. When a
   * tier is active and the variant isn't already on sale, the pre-rendered
   * compare-at price is revealed and set to the base (qty-1) price. When the
   * variant is already on sale, its compare-at is left as-is.
   * @param {{ priceFormatted: string } | null} active - The active tier, if any.
   */
  #updatePrice(active) {
    const alreadyOnSale = this.dataset.vpOnSale === 'true';

    for (const priceElement of this.#priceElements()) {
      const container = priceElement.querySelector('[ref="priceContainer"]');
      if (!(container instanceof HTMLElement)) continue;

      const priceSpan = container.querySelector('.price');
      if (!(priceSpan instanceof HTMLElement)) continue;

      // Cache the server-rendered base price the first time we touch this span.
      if (!priceSpan.dataset.vpBase) {
        priceSpan.dataset.vpBase = priceSpan.innerText;
      }
      // priceSpan.innerHTML = active
      //   ? active.priceFormatted
      //   : priceSpan.dataset.vpBase;
      if (active?.priceFormatted || priceSpan.dataset.vpBase) {
        const displayPrice = active?.priceFormatted || priceSpan.dataset.vpBase;
        priceSpan.innerText = displayPrice;
      }
      // When already on sale, the compare-at is shown by the server; leave it be.
      if (alreadyOnSale) continue;

      const compareEl = container.querySelector('.compare-at-price');
      if (!(compareEl instanceof HTMLElement)) continue;

      const compareMoney = compareEl.querySelector('.money');
      if (active) {
        if (compareMoney instanceof HTMLElement) {
          if (compareMoney.dataset.vpBase == null) {
            compareMoney.dataset.vpBase = compareMoney.innerText;
          }
          compareMoney.innerText = priceSpan.dataset.vpBase;
        }
        compareEl.classList.remove('hidden');
      } else {
        compareEl.classList.add('hidden');
        if (
          compareMoney instanceof HTMLElement &&
          compareMoney.dataset.vpBase != null
        ) {
          compareMoney.innerHTML = compareMoney.dataset.vpBase;
        }
      }
    }
  }

  /**
   * Announces the active tier to screen readers.
   * @param {{ min: number, priceFormatted: string } | null} active - The active tier.
   */
  #announce(active) {
    const liveRegion = this.querySelector('[data-vp-live]');
    if (!(liveRegion instanceof HTMLElement)) return;

    liveRegion.textContent = '';
    if (!active) return;

    requestAnimationFrame(() => {
      liveRegion.textContent = `Volume price applied: ${active.priceFormatted} each at ${active.min} or more.`;
    });
  }
}

if (!customElements.get('tw-volume-pricing')) {
  customElements.define('tw-volume-pricing', VolumePricing);
}
